import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './Design/CourseTestPage.css';
import { shuffleArray } from 'src/handler/Helper';
import { Button } from '@mui/material';
import { GetCoursesForExam } from 'src/handler/coursehandler';

interface Course {
    courseId: number;
    courseName: string;
    serverName: string;
    examEligible: boolean;
    attemptCount: number;
    attemptTime: string;
    isPassed: boolean;
}

interface CourseTest {
    courseTestId: number;
    courseId: number;
    courseQuestion: string;
    option1: string;
    option2: string;
    option3: string;
    option4: string;
    answerOption: number;
}

const PASSING_PERCENTAGE = 70;
const QUESTION_TIME = 15;

const CourseTestSlider: React.FC = () => {
    const [courses, setCourses] = useState<Course[]>([]);
    const [selectedCourses, setSelectedCourses] = useState<Course>();
    const [selectedCourseId, setSelectedCourseId] = useState<number | null>(null);
    const [questions, setQuestions] = useState<CourseTest[]>([]);
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [answers, setAnswers] = useState<Record<number, number>>({});
    const [timer, setTimer] = useState<number>(QUESTION_TIME);
    const [submitted, setSubmitted] = useState<boolean>(false);
    const [score, setScore] = useState<number>(0);
    const [transitioning, setTransitioning] = useState<boolean>(false);
    const [startExam, setStartExam] = useState(false);
    const [examEligible, setExamEligible] = useState(false);
    const [userInformationId, setInformationId] = useState(0);
    const [isPassed, setIsPassed] = useState(false);

    // Fetch course list on mount
    useEffect(() => {
        
        const value = localStorage.getItem("userProfile");

        const fetchCourses = async () => {
            try {
                const response = await GetCoursesForExam({
                    "courseTestEnum": 1
                });
                if (response.statusCode === 200) {
                    setCourses(response.data.course);
                }
            } catch (error) {
                console.error("Error fetching courses:", error);
            }
        };

        fetchCourses();
    }, []);


    const handleStart = async () => {
        if (new Date().getTime() - new Date(selectedCourses?.attemptTime ?? "").getTime() > timingToStay(selectedCourses?.attemptCount ?? 0)) {
            var response = await GetCoursesForExam({
                "courseId": selectedCourseId,
                "courseTestEnum": 2,
                "totalQuestions": 0,
                "correctAnswers": 0
            })
            if (response.statusCode === 200) {
                const shuffled = shuffleArray(response.data.courseTest) as CourseTest[];
                setQuestions(shuffled);
                setStartExam(true);
            }
        }
        else {
            alert("sorry not eligible");
        }
    }

    useEffect(() => {
        if (submitted || currentIndex >= questions.length) return;

        const interval = setInterval(() => {
            setTimer((prev) => {
                if (prev === 1) {
                    handleNext();
                    return QUESTION_TIME;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [currentIndex, submitted, questions.length]);

    const handleOptionSelect = (option: number) => {
        const questionId = questions[currentIndex].courseTestId;
        setAnswers((prev) => ({ ...prev, [questionId]: option }));
    };

    const handleNext = () => {
        setTransitioning(true);
        setTimeout(() => {
            setTransitioning(false);
            if (currentIndex < questions.length - 1) {
                setCurrentIndex(currentIndex + 1);
                setTimer(QUESTION_TIME);
            } else {
                handleSubmit();
            }
        }, 600);
    };

    const handleSubmit = async () => {
        
        let correct = 0;
        let courseId = 0;
        questions.forEach((q) => {
            if (answers[q.courseTestId] === q.answerOption) {
                correct++;
                courseId = q.courseId
            }
        });
        await GetCoursesForExam(
            {
                "courseId": courseId,
                "courseTestEnum": 3,
                "totalQuestions": questions.length,
                "correctAnswers": correct
            }
        )
        setScore(correct);
        setSubmitted(true);
    };

    const percentage = ((score / questions.length) * 100).toFixed(2);
    const passed = parseFloat(percentage) >= PASSING_PERCENTAGE;

    const timingToStay = (attemptCount: number): number => {
        switch (attemptCount) {
            case 1:
                return 24 * 60 * 60 * 1000; // 24 hours
            case 2:
                return 48 * 60 * 60 * 1000; // 48 hours
            case 3:
                return 72 * 60 * 60 * 1000; // 72 hours
            default:
                return 0; // fallback if attemptCount is outside expected range
        }
    }

    // Prevent refresh/close
    useEffect(() => {
        if (!startExam) return;
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "Exam in progress. Are you sure you want to leave?";
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [startExam]);

    useEffect(() => {
        if (!startExam) return;

        const blockContextMenu = (e: MouseEvent) => e.preventDefault();
        const blockKeys = (e: KeyboardEvent) => {
            const key = e.key.toLowerCase();
            if (e.ctrlKey && ["c", "p", "s", "u"].includes(key)) {
                e.preventDefault();
                alert("Copy/Print disabled during exam!");
            }
            if (key === "printscreen") {
                e.preventDefault();
                alert("Screenshots disabled during exam!");
            }
        };

        document.addEventListener("contextmenu", blockContextMenu);
        document.addEventListener("keydown", blockKeys);

        return () => {
            document.removeEventListener("contextmenu", blockContextMenu);
            document.removeEventListener("keydown", blockKeys);
        };
    }, [startExam]);
    // Show dropdown if no course selected
    if (startExam === false) {
        return (
            <div className="course-selection growniverseBackground">
                <div className="course-card">
                    <h2 className="course-title">🎓 Choose Your Course</h2>

                    <div className="dropdown-wrapper">
                        <label htmlFor="courseDropdown" className="dropdown-label">Available Courses</label>
                        <select
                            id="courseDropdown"
                            className="course-dropdown"
                            defaultValue=""
                            onChange={(e) => {
                                const selectedId = Number(e.target.value);
                                const selectedCourse = courses.find(course => course.courseId === selectedId);
                                setSelectedCourseId(selectedId);
                                setSelectedCourses(selectedCourse);
                                setExamEligible(selectedCourse?.examEligible === true); // ensures it's a boolean
                            }}
                        >
                            <option value="" disabled>Select a course</option>
                            {courses.map(course => (
                                <option
                                    style={course.isPassed ? { background: "green", color: "white" }
                                        : selectedCourseId === course.courseId ? { background: "skyblue" } : {}}
                                    key={course.courseId} value={course.courseId} disabled={new Date().getTime() - new Date(course?.attemptTime ?? "").getTime() < timingToStay(course.attemptCount) || course.attemptCount > 3 || course.isPassed}>
                                    {course.courseName} ({course.isPassed ? "Passed" :
                                        (
                                            new Date().getTime() - new Date(course?.attemptTime ?? "").getTime() > timingToStay(course.attemptCount) && course.attemptCount < 3
                                                ? (3 - course.attemptCount) + " Attempt Left"
                                                : course.attemptCount === 1 ? "Try After 24 Hours" :
                                                    course.attemptCount === 2 ? "Try After 48 Hours" :
                                                        course.attemptCount === 3 ? "Try After 72 Hours "
                                                            : "Failed"
                                        )})
                                </option>
                            ))}
                        </select>
                    </div>
                    <button className="start-button" onClick={handleStart}>
                        🚀 Start Exam
                    </button>
                </div>
            </div>
        );
    }

    if (questions.length === 0) return <div className="loader">Initializing Exam...</div>;

    if (submitted) {
        return (
            <div className="result-screen">
                <h1 className="result-title">Result ✅</h1>
                <div className="score-box">
                    <p>Score: {score} / {questions.length}</p>
                    <p>Accuracy: {percentage}%</p>
                    <p>Status: <span className={passed ? 'pass' : 'fail'}>{passed ? 'Passed' : 'Failed'}</span></p>
                </div>
            </div>
        );
    }

    const currentQuestion = questions[currentIndex];

    return (
        <div className='growniverseBackground' style={{ padding: "30px", display: "flex", justifyContent: "center" }}>
            <div className={`question-card ${transitioning ? 'slide-out' : 'slide-in'}`}>
                <div className="timer">⏳ {timer}s</div>
                <h2 className="question-count">Q{currentIndex + 1} of {questions.length}</h2>
                <h3 className="question-text"><span style={{ color: "#00ffcc" }}>Q{currentIndex + 1} :</span> {currentQuestion.courseQuestion}</h3>

                <div className="options-grid">
                    {[currentQuestion.option1, currentQuestion.option2, currentQuestion.option3, currentQuestion.option4].map((opt, index) => (
                        <div key={index} className="option-wrapper">
                            <label className="option" style={answers[currentQuestion.courseTestId] === index + 1 ? { fontWeight: "bold" } : {}}>
                                <input
                                    type="radio"
                                    name={`question-${currentQuestion.courseTestId}`}
                                    value={index + 1}
                                    checked={answers[currentQuestion.courseTestId] === index + 1}
                                    onChange={() => handleOptionSelect(index + 1)}
                                />
                                {opt}
                            </label>
                        </div>
                    ))}
                </div>

                <button onClick={handleNext} className="next-button">
                    {currentIndex === questions.length - 1 ? 'Submit Exam' : 'Next'}
                </button>
            </div>
        </div>
    );
};

export default CourseTestSlider;